<template>
  <v-navigation-drawer
    v-model="layout.drawer"
    :location="isArabic ? 'right' : 'left'"
    width="305"
    app
    class="sidebar"
    :class="{
      'sidebar-rtl': isArabic,
      'sidebar-ltr': !isArabic,
    }"
    :dir="isArabic ? 'rtl' : 'ltr'"
  >
    <v-list nav density="compact" class="sidebar-list" @click="closeDrawer">
      <template v-for="group in visibleGroups" :key="group.title">
        <div class="group-label">
          {{ $t(group.title) }}
        </div>

        <v-list-item
          v-for="item in group.items"
          :key="item.to"
          :title="$t(item.title)"
          :to="item.to"
          :prepend-icon="item.icon"
          class="nav-item"
        />
      </template>

      <div
        v-if="authorizationLoaded && visibleGroups.length === 0"
        class="no-permissions"
      >
        <v-icon size="32"> mdi-shield-alert-outline </v-icon>

        <span>
          {{
            isArabic
              ? "لا توجد قوائم متاحة لهذا المستخدم"
              : "No menus available for this user"
          }}
        </span>
      </div>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";

import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { useLayoutStore } from "@/stores/layout";

const layout = useLayoutStore();
const { locale } = useI18n();
const { mobile } = useDisplay();

const currentRole = ref(null);
const currentPermissions = ref([]);
const authorizationLoaded = ref(false);

const isArabic = computed(() => locale.value === "ar");

const adminRoles = ["admin", "admin_app"];

/**
 * قراءة JSON من localStorage.
 */
const readJson = (key, fallback = null) => {
  try {
    const value = localStorage.getItem(key);

    if (value === null || value === "") {
      return fallback;
    }

    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/**
 * استخراج كود الدور.
 */
const currentRoleCode = computed(() => {
  if (typeof currentRole.value === "string") {
    return currentRole.value.toLowerCase().trim();
  }

  return String(currentRole.value?.code ?? "")
    .toLowerCase()
    .trim();
});

/**
 * استخراج كود الصلاحية.
 */
const getPermissionCode = (permission) => {
  if (typeof permission === "string") {
    return permission.trim();
  }

  return String(permission?.code ?? "").trim();
};

/**
 * تنظيف الصلاحيات ومنع التكرار.
 */
const normalizePermissions = (permissions) => {
  if (!Array.isArray(permissions)) {
    return [];
  }

  const uniquePermissions = new Map();

  permissions.forEach((permission) => {
    const code = getPermissionCode(permission);

    if (code) {
      uniquePermissions.set(code, permission);
    }
  });

  return Array.from(uniquePermissions.values());
};

/**
 * تحميل الدور والصلاحيات.
 */
const loadAuthorization = () => {
  authorizationLoaded.value = false;

  const storedUser = readJson("user", null);
  const storedRole = readJson("role", null);
  const storedPermissions = readJson("permissions", []);

  currentRole.value = storedRole ?? storedUser?.role ?? null;

  if (Array.isArray(storedPermissions) && storedPermissions.length > 0) {
    currentPermissions.value = normalizePermissions(storedPermissions);
  } else if (Array.isArray(storedUser?.role?.permissions)) {
    currentPermissions.value = normalizePermissions(
      storedUser.role.permissions
    );
  } else if (Array.isArray(storedUser?.permissions)) {
    currentPermissions.value = normalizePermissions(storedUser.permissions);
  } else {
    currentPermissions.value = [];
  }

  authorizationLoaded.value = true;
};

/**
 * التحقق من الصلاحية.
 */
const can = (permissionCode) => {
  if (!permissionCode) {
    return true;
  }

  if (adminRoles.includes(currentRoleCode.value)) {
    return true;
  }

  return currentPermissions.value.some(
    (permission) => getPermissionCode(permission) === permissionCode
  );
};

/**
 * عناصر القائمة والصلاحية المطلوبة لكل عنصر.
 */
const menuGroups = computed(() => [
  {
    title: "sidebar.overview",
    items: [
      {
        title: "sidebar.dashboard",
        to: "/home",
        icon: "mdi-view-dashboard-outline",
        permission: "dashboard.view",
      },
      {
        title: "sidebar.nationalScope",
        to: "/national",
        icon: "mdi-earth",
        permission: "national.view",
      },
      {
        title: "sidebar.interactiveMap",
        to: "/map",
        icon: "mdi-map-outline",
        permission: "map.view",
      },
    ],
  },

  {
    title: "sidebar.analysisSupport",
    items: [
      {
        title: "sidebar.facilities",
        to: "/facilities",
        icon: "mdi-home-city-outline",
        permission: "facilities.view",
      },
      {
        title: "sidebar.priorityEngine",
        to: "/priorities",
        icon: "mdi-format-list-bulleted",
        permission: "priorities.view",
      },
      {
        title: "sidebar.scenarios",
        to: "/scenarios",
        icon: "mdi-alert-outline",
        permission: "scenarios.view",
      },
      {
        title: "sidebar.investment",
        to: "/investment",
        icon: "mdi-currency-usd",
        permission: "investment.view",
      },
    ],
  },

  {
    title: "sidebar.danaAxes",
    items: [
      {
        title: "sidebar.allAxes",
        to: "/assessment",
        icon: "mdi-bank-outline",
        permission: "assessment.view",
      },
      {
        title: "sidebar.infrastructure",
        to: "/assessment/infrastructure/structural",
        icon: "mdi-home-outline",
        permission: "infrastructure.view",
      },
      {
        title: "sidebar.services",
        to: "/assessment/services/availability",
        icon: "mdi-stethoscope",
        permission: "services.view",
      },
      {
        title: "sidebar.workforce",
        to: "/assessment/staff/summary",
        icon: "mdi-account-group-outline",
        permission: "workforce.view",
      },
      {
        title: "sidebar.finance",
        to: "/assessment/finance/status",
        icon: "mdi-currency-usd",
        permission: "finance.view",
      },
      {
        title: "sidebar.governance",
        to: "/assessment/governance/structure",
        icon: "mdi-bank-outline",
        permission: "governance.view",
      },
      {
        title: "sidebar.equity",
        to: "/equity",
        icon: "mdi-heart-outline",
        permission: "equity.view",
      },
      {
        title: "sidebar.ragBoard",
        to: "/rag",
        icon: "mdi-table-large",
        permission: "rag.view",
      },
    ],
  },

  {
    title: "sidebar.outputs",
    items: [
      {
        title: "sidebar.reports",
        to: "/reports",
        icon: "mdi-file-document-outline",
        permission: "reports.view",
      },
      {
        title: "sidebar.exportData",
        to: "/export",
        icon: "mdi-download",
        permission: "export.view",
      },
      {
        title: "sidebar.database",
        to: "/database",
        icon: "mdi-database-outline",
        permission: "database.view",
      },
      {
        title: "sidebar.methodology",
        to: "/methodology",
        icon: "mdi-book-outline",
        permission: "methodology.view",
      },
      {
        title: "sidebar.users",
        to: "/users",
        icon: "mdi-account-group-outline",
        permission: "users.manage",
      },
      {
        title: "sidebar.about",
        to: "/about",
        icon: "mdi-information-outline",
        permission: "about.view",
      },
    ],
  },
]);

/**
 * إظهار العناصر المسموح بها فقط.
 * المجموعة الفارغة تُخفى بالكامل.
 */
const visibleGroups = computed(() => {
  if (!authorizationLoaded.value) {
    return [];
  }

  return menuGroups.value
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => can(item.permission)),
    }))
    .filter((group) => group.items.length > 0);
});

const closeDrawer = () => {
  if (mobile.value) {
    layout.drawer = false;
  }
};

const handleStorageChange = (event) => {
  if (
    event.key === null ||
    ["user", "role", "permissions"].includes(event.key)
  ) {
    loadAuthorization();
  }
};

const handleAuthorizationUpdate = () => {
  loadAuthorization();
};

onMounted(() => {
  loadAuthorization();

  window.addEventListener("storage", handleStorageChange);

  window.addEventListener("authorization-updated", handleAuthorizationUpdate);
});

onUnmounted(() => {
  window.removeEventListener("storage", handleStorageChange);

  window.removeEventListener(
    "authorization-updated",
    handleAuthorizationUpdate
  );
});
</script>

<style scoped>
.sidebar {
  background: #ffffff !important;
  color: #526174 !important;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.sidebar-rtl {
  direction: rtl;
  border-left: 1px solid #e5eaf0 !important;
  border-right: none !important;
}

.sidebar-ltr {
  direction: ltr;
  border-right: 1px solid #e5eaf0 !important;
  border-left: none !important;
}

.sidebar-list {
  padding: 28px 12px 24px !important;
}

.group-label {
  padding: 16px 18px 9px;
  color: #8a96a8;
  font-size: 12px;
  font-weight: 800;
}

.sidebar-rtl .group-label {
  text-align: right;
}

.sidebar-ltr .group-label {
  text-align: left;
}

.nav-item {
  min-height: 46px !important;
  margin-bottom: 5px !important;
  padding-inline: 12px !important;
  color: #526174 !important;
  border-radius: 9px !important;
}

.sidebar-rtl :deep(.v-list-item__content) {
  text-align: right !important;
}

.sidebar-ltr :deep(.v-list-item__content) {
  text-align: left !important;
}

:deep(.v-list-item-title) {
  color: inherit !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  line-height: 1.5 !important;
}

:deep(.v-list-item__prepend) {
  margin-inline-start: 0 !important;
  margin-inline-end: 12px !important;
}

:deep(.v-list-item__prepend .v-icon) {
  color: inherit !important;
  font-size: 22px !important;
}

.nav-item:hover {
  background: #f5f8fb !important;
  color: #173f73 !important;
}

:deep(.v-list-item--active) {
  background: #173f73 !important;
  color: #ffffff !important;
  border: 2px solid #00a8e8 !important;
  box-shadow: inset 0 0 0 1px #ffffff;
}

:deep(.v-list-item--active .v-list-item-title),
:deep(.v-list-item--active .v-icon) {
  color: #ffffff !important;
  font-weight: 900 !important;
}

.no-permissions {
  display: flex;
  min-height: 180px;
  padding: 30px 18px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}

:deep(.v-navigation-drawer__content) {
  overflow-x: hidden !important;
  overflow-y: auto !important;
}

:deep(.v-navigation-drawer__content::-webkit-scrollbar) {
  width: 5px;
}

:deep(.v-navigation-drawer__content::-webkit-scrollbar-thumb) {
  background: #cbd5e1;
  border-radius: 20px;
}

:global(html[data-theme="dark"]) .sidebar {
  background: #111827 !important;
  color: #e2e8f0 !important;
}

:global(html[data-theme="dark"]) .sidebar-rtl {
  border-left-color: #334155 !important;
}

:global(html[data-theme="dark"]) .sidebar-ltr {
  border-right-color: #334155 !important;
}

:global(html[data-theme="dark"]) .group-label {
  color: #94a3b8 !important;
}

:global(html[data-theme="dark"]) .nav-item {
  color: #cbd5e1 !important;
}

:global(html[data-theme="dark"]) .nav-item:hover {
  background: #1e293b !important;
  color: #ffffff !important;
}

:global(html[data-theme="dark"]) .no-permissions {
  color: #94a3b8 !important;
}
</style>
